import axios, { AxiosError } from 'axios';
import toastService from './toastService';
import keycloakService from './keycloak';
import spazioService from './spazioService';
import permessiService from './permessiService';
import { API_BASE_URL as CONFIG_API_BASE_URL } from "@/config";
import { vediCome, INTESTAZIONE as INTESTAZIONE_VEDI_COME, CODICE_TERMINATO as VEDI_COME_TERMINATO } from './vediCome';

const API_BASE_URL = CONFIG_API_BASE_URL;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Un errore senza risposta non e' il server che dice qualcosa: e' la richiesta che non e'
 * arrivata. Rete assente, radio del telefono che si riaggancia, backend che si sta riavviando
 * durante un rilascio.
 *
 * Vale la pena distinguerlo perche' merita un trattamento opposto a un errore vero: un vero
 * errore va detto subito, questo va ritentato in silenzio, perche' quasi sempre passa da se'.
 */
function erroreDiRete(error) {
  return !error.response && error.code !== 'ERR_CANCELED';
}

/** Attesa breve fra un tentativo e l'altro. */
function attendi(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * La chiamata non si puo' fare perche' il token e' scaduto e la rete non ha permesso di
 * rinnovarlo: la si fa fallire come un errore di rete vero, cosi' chi ha chiamato, il toast e i
 * sondaggi di sfondo la trattano come tale. Mai un'uscita.
 *
 * __ritentata: il ritentativo di rete qui sotto e' gia' stato fatto, dentro il rinnovo (1, 2 e
 * 4 secondi); rifarlo allungherebbe solo l'attesa. La richiesta ({}) c'e' perche' toastService
 * riconosce l'errore di rete da error.request.
 */
function erroreRinnovoSenzaRete(config) {
  if (config) config.__ritentata = true;
  return new AxiosError('Rinnovo del token non riuscito: rete assente', AxiosError.ERR_NETWORK, config, {});
}

/** Il token con cui e' partita la chiamata, per capire se nel frattempo qualcuno l'ha rinnovato. */
function tokenDellaChiamata(config) {
  const intestazione = config?.headers?.Authorization || config?.headers?.authorization || '';
  return String(intestazione).replace(/^Bearer\s+/i, '') || null;
}

/**
 * Il server ha risposto 401: non ha riconosciuto il token (mancante, scaduto, non valido).
 *
 * Prima si usciva subito, al primo 401, e nell'app l'uscita revocava anche la sessione di trenta
 * giorni: bastava una chiamata partita con il token scaduto alla riapertura. Adesso un 401 e' una
 * domanda, non un verdetto: si rinnova (una volta sola per tutte le chiamate ferme, vedi
 * keycloak.js) e si ripete la chiamata UNA volta.
 * - rinnovo riuscito: si ripete, con il token nuovo che mette l'intercettore di richiesta;
 * - rete: errore di rete, nessuna uscita;
 * - Keycloak rifiuta il rinnovo: si esce (tranne da una pagina pubblica);
 * - 401 anche sulla ripetizione, con un token appena rinnovato: il problema non e' il token in
 *   mano, si esce. Il segno __dopoRinnovo sulla config impedisce il giro infinito.
 *
 * RIPETERE E' SICURO ANCHE PER UNA POST NON IDEMPOTENTE: il 401 lo da' la catena di sicurezza di
 * Spring PRIMA di arrivare al controller, quindi il server quella richiesta non l'ha eseguita.
 * L'unica eccezione era il 401 di Channex girato tale e quale da GlobalExceptionHandler: e' un
 * errore di un servizio esterno, non della nostra sessione, e il backend ora lo risponde 502.
 * Per questo il backend va rilasciato PRIMA (o insieme) di questo file e dell'APK: con un backend
 * vecchio quella chiamata si ripeterebbe una volta, e una POST potrebbe aver gia' scritto qualcosa.
 *
 * I 403 (SECONDO_FATTORE_RICHIESTO, AREA_NON_CONSENTITA e gli altri) non passano di qui.
 */
async function gestisci401(error) {
  const config = error.config;
  const percorso = config?.url || null;

  if (!config) {
    keycloakService.sessioneFinita('401', { percorso });
    return Promise.reject(error);
  }
  if (config.__dopoRinnovo) {
    keycloakService.sessioneFinita('401_dopo_rinnovo', { percorso });
    return Promise.reject(error);
  }
  config.__dopoRinnovo = true;

  const esito = await keycloakService.rinnovaDopo401(tokenDellaChiamata(config));
  if (esito === 'ok') {
    return apiClient.request(config);
  }
  if (esito === 'rete') {
    const diRete = erroreRinnovoSenzaRete(config);
    if (!config.skipToast) {
      toastService.apiError(diRete);
    }
    return Promise.reject(diRete);
  }
  keycloakService.sessioneFinita('rinnovo_rifiutato', { percorso });
  return Promise.reject(error);
}

// Un solo rientro alla volta: una pagina negata fa partire tutte le sue chiamate insieme e
// tornerebbero tutte con lo stesso 403. Senza guardia sarebbero N navigazioni una sull'altra.
let rientroInCorso = false;

/**
 * Il codice applicativo dell'errore, se c'e'.
 *
 * Con responseType 'blob' (i nove scarichi di file del progetto) il corpo dell'errore arriva
 * come Blob, quindi data.error e' undefined e i controlli sui codici non vedrebbero nulla:
 * in quel caso si legge il testo e si prova a interpretarlo come JSON.
 */
async function codiceErrore(error) {
  const dati = error.response?.data;
  if (!dati) return null;

  if (typeof Blob !== 'undefined' && dati instanceof Blob) {
    try {
      const testo = await dati.text();
      return JSON.parse(testo)?.error?.code || null;
    } catch (e) {
      // Blob binario davvero (un PDF, uno zip): nessun codice da leggere.
      return null;
    }
  }

  return dati?.error?.code || null;
}

/**
 * Il ruolo non copre l'area della pagina (FiltroPermessiRuolo, HTTP 403 AREA_NON_CONSENTITA).
 * Non e' un guasto e non e' un errore da toast ripetuto — ne uscirebbe uno per ogni chiamata
 * della pagina: e' "questa pagina non e' per te". Si riporta la persona dove puo' stare.
 */
async function rientraSuPaginaConsentita() {
  if (rientroInCorso) return;
  rientroInCorso = true;
  try {
    // Il profilo e' in cache: /account/me sta nell'area ACCOUNT, consentita a ogni ruolo,
    // quindi questa chiamata non puo' rimbalzare a sua volta con lo stesso 403.
    const ruolo = await permessiService.ruolo();
    const destinazione = permessiService.paginaIniziale(ruolo);
    const corrente = window.location.pathname;

    // Se siamo gia' sulla destinazione, il 403 arriva da li': mandarci di nuovo sarebbe un
    // giro infinito di ricariche. Meglio fermarsi e lasciare la pagina com'e'.
    if (corrente === destinazione || corrente.startsWith(`${destinazione}/`)) {
      return;
    }

    console.warn('Area non consentita al ruolo: si rientra su', destinazione);
    window.location.assign(destinazione);
  } finally {
    // Se non si e' navigato la guardia va liberata, altrimenti un cambio pagina successivo
    // resterebbe senza rientro per tutta la vita della scheda.
    rientroInCorso = false;
  }
}

// I metodi che il server conta come letture (FiltroPermessiRuolo): tutto il resto e' scrittura.
const METODI_DI_LETTURA = ['get', 'head', 'options'];

/**
 * Un 403 AREA_NON_CONSENTITA dice che il ruolo non copre l'area di QUELLA chiamata, non per
 * forza della pagina. Una pagina consentita puo' toccare un'area che il ruolo non ha — il
 * calendario del proprietario, con la modale di una prenotazione aperta sopra: il percorso
 * resta /pms — e rimandare la persona alla pagina iniziale per questo la portava via da dove
 * poteva stare.
 *
 * Quindi si guarda la pagina, non la chiamata. Se e' la pagina a non essere consentita si rientra
 * come prima. Altrimenti si resta: una lettura negata tace, perche' e' un dato di contorno che
 * nessuno ha chiesto e la pagina mostra quello che ha; una scrittura negata e' invece un gesto
 * della persona, che deve sapere che non e' andato a buon fine. La promessa si rifiuta comunque:
 * cosa mostrare lo decide chi ha chiamato.
 */
async function areaNonConsentita(error) {
  // Profilo in cache, e /account/me e' area ACCOUNT: questa chiamata non rimbalza qui.
  const ruolo = await permessiService.ruolo();
  if (!permessiService.puoAprire(ruolo, window.location.pathname)) {
    rientraSuPaginaConsentita();
    return;
  }
  const metodo = (error.config?.method || 'get').toLowerCase();
  if (!METODI_DI_LETTURA.includes(metodo) && !error.config?.skipToast) {
    toastService.apiError(error);
  }
}

// Request interceptor per aggiungere token JWT da Keycloak
apiClient.interceptors.request.use(
  async (config) => {
    // Quale spazio di lavoro sta guardando la persona. Se non lo sappiamo ancora si lascia
    // decidere al server, che ripiega sull'ultimo spazio usato.
    const spazio = spazioService.getAttivo();
    if (spazio) {
      config.headers['X-Spazio'] = spazio;
    }

    // "Vedi come": su ogni chiamata di questa scheda. E' il backend a decidere quali restano di chi
    // amministra (la fine del "vedi come", il suo secondo fattore), e da chi non amministra la ignora.
    const guardato = vediCome.stato();
    if (guardato) {
      config.headers[INTESTAZIONE_VEDI_COME] = guardato.persona;
    }

    try {
      // Ensure token is fresh before each request
      const token = await keycloakService.ensureValidToken({ percorso: config.url });
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      // Token scaduto e rete che non lascia rinnovarlo: la chiamata non parte, e fallisce come
      // errore di rete (passa comunque dall'intercettore di risposta, che mostra il toast).
      if (keycloakService.eRinnovoSenzaRete(error)) {
        throw erroreRinnovoSenzaRete(config);
      }
      // Qualunque altro guaio qui e' nostro, non della sessione: prima faceva uscire, adesso si
      // annota e la chiamata parte com'e'. Se il server non la accetta rispondera' 401, e il 401
      // ha la sua gestione, che prima prova a rinnovare.
      console.error('Rinnovo del token: errore inatteso, la chiamata parte senza rinnovo', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor per gestire errori
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Prima di tutto il resto: se non c'e' stata risposta si ritenta una volta sola, dopo una
    // pausa breve. Copre i due casi che si vedevano davvero — il telefono che riapre il browser
    // mentre la rete si riaggancia, e i secondi in cui il backend si riavvia per un rilascio.
    // Una volta sola di proposito: se non passa al secondo colpo non e' un'increspatura, e
    // insistere non farebbe che ritardare il messaggio a chi sta aspettando.
    if (erroreDiRete(error) && error.config && !error.config.__ritentata) {
      error.config.__ritentata = true;
      await attendi(1500);
      return apiClient.request(error.config);
    }

    // Gestisci 401 - Unauthorized: rinnovo e una ripetizione, prima di pensare all'uscita.
    if (error.response?.status === 401) {
      return gestisci401(error);
    }

    const codice = await codiceErrore(error);

    // Il "vedi come" non c'e' piu' (chiuso, fermo da troppo, la persona e' stata sospesa): si torna se
    // stessi, invece di restare davanti a dati che nessuno sa piu' di chi siano.
    if (codice === VEDI_COME_TERMINATO && vediCome.attivo()) {
      vediCome.chiudiConAvviso(error.response?.data?.error?.message || 'Non stai più guardando come questa persona.');
      window.location.assign('/');
      return Promise.reject(error);
    }

    // Lo spazio che stiamo chiedendo non e' (piu') nostro: non e' un errore da mostrare, e'
    // uno stato locale sbagliato. Si dimentica lo spazio salvato e si riparte da capo, senza
    // toast: la pagina sta gia' andando via e un messaggio rosso spaventerebbe e basta.
    if (codice === 'SPAZIO_NON_CONSENTITO') {
      spazioService.ripartiSenzaSpazio();
      return Promise.reject(error);
    }

    // Il gemello: lo spazio e' nostro, ma l'area no.
    if (codice === 'AREA_NON_CONSENTITA') {
      await areaNonConsentita(error);
      return Promise.reject(error);
    }

    // La password c'e', il secondo fattore no. Non e' un errore da mostrare: e' un passaggio
    // che manca, e la risposta e' portare la persona dove lo si completa.
    //
    // Il backend risponde 403 e non 401 di proposito: un 401 qui sopra porta al rinnovo e,
    // se non basta, al logout, quindi la persona perderebbe i token e tornerebbe alla pagina
    // d'accesso, dove rientrerebbe per ritrovarsi nello stesso punto — un giro senza fine. Il
    // codice esplicito rompe il giro.
    if (codice === 'SECONDO_FATTORE_RICHIESTO') {
      // pathname + search: senza la query si tornerebbe sulla pagina giusta ma senza
      // i suoi parametri, e chi stava guardando un periodo o un filtro se lo ritrova
      // azzerato dopo aver dato il codice.
      const corrente = window.location.pathname + window.location.search;
      if (!corrente.startsWith('/secondo-fattore')) {
        const dove = corrente === '/' ? '' : `?redirect=${encodeURIComponent(corrente)}`;
        window.location.assign(`/secondo-fattore${dove}`);
      }
      return Promise.reject(error);
    }

    // Mostra toast per tutti gli altri errori
    // skipToast può essere usato per evitare il toast automatico
    //
    // Anche gli errori di rete parlano, ma solo dopo che il ritentativo qui sopra ha fallito:
    // chi ha premuto "Salva" senza campo deve saperlo, altrimenti crede di aver salvato. A
    // tacere sono i sondaggi di sfondo, che passano skipToast perche' nessuno ha chiesto
    // niente — ed e' esattamente quello che parte quando si riapre il browser sul telefono.
    if (!error.config?.skipToast) {
      toastService.apiError(error);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
